// The two-row statistics line of the WebGL2 client (plan C.2 `fast/stats.ts`).
//
// It writes the SAME two sentences the shipping client writes,
// `S.stats_wire` and `S.stats_render` from `gen/strings.ts`, so the two
// clients can be read side by side without translating one into the other.
// Three cells say which client it is:
//
//   gl       the sentence states `gl` where the reference stated its zoom
//            band: there is no zoom band here. The picture is
//            resolution-independent by construction, which is the whole
//            claim the prototype makes (R188, R191 gave the reference its
//            bands because live vector art was too dear zoomed in).
//   backend  `webgl2 fast` or `webgpu fast`, where the reference writes
//            `webgpu cache:on`. It names the backend that drew the frame.
//   the tail the draw calls, the instances and the GPU milliseconds, which
//            the reference has no counter for.
//
// The sweep is the engine's own: `Loop` calls this once a second with the
// frame statistics it accumulated, and the wire figures are read here
// against the same wall-clock second.

import type { Connection } from '../net/connection';
import type { GameState } from '../state';
import type { Overlay } from '../ui/overlay';
import { main as S } from '../gen/strings';
import type { FrameStats } from './layer';
import type { Backend } from './gfx/types';

/** What the sweep reads. Every one of them is late-built, so all are lazy. */
export interface StatsDeps {
  ui: () => Overlay;
  /** Which graphics API drew the frame. `backendCell` names it. */
  gfx: () => { backend: Backend };
  conn: () => Connection;
  state: GameState;
  /** The tick of the last state frame. */
  tick: () => number;
  /** How many contacts the client holds. M2 answers it from the fleet layer. */
  contacts: () => number;
  /** The camera scale, as the top bar prints it. */
  zoom: () => number;
  /**
   * Anything else that belongs on the one-second sweep. The shipping client
   * re-applies the audio switches and pushes the media card's clock here,
   * because neither needs a frame's precision (`main.ts` ticker).
   *
   * It runs before the wire figures are read, and it runs on an offline
   * sweep too: the music must not stop because the socket did.
   */
  onSweep?: () => void;
}

/**
 * The backend half of the backend cell.
 *
 * It names the graphics API the picture is drawn through. The client asks
 * for WebGPU where the page or the default says so and falls back to WebGL2,
 * so the cell is read from the live surface and never from a constant.
 */
export function backendLabel(gfx: { backend: Backend }): string {
  return `${gfx.backend} fast`;
}

/**
 * The engine's `onStats` sink.
 *
 * `line` is the engine's own console-shaped summary; this writes the DOM
 * rows instead, from the same `FrameStats`.
 */
export function makeStatsSink(deps: StatsDeps) {
  let at = performance.now();
  return (_line: string, stats: FrameStats): void => {
    const now = performance.now();
    const elapsed = Math.max(1e-3, (now - at) / 1000);
    at = now;
    deps.onSweep?.();

    const conn = deps.conn();
    const ui = deps.ui();
    if (!conn.connected) {
      ui.setStats(S.stats_offline({ retries: conn.retries }));
      ui.render();
      return;
    }
    const wire = conn.takeStats();
    ui.setStats(
      S.stats_wire({
        tick: deps.tick(),
        contacts: deps.contacts(),
        rtt: conn.rttMs.toFixed(0),
        kbs: (wire.bytesIn / 1024 / elapsed).toFixed(1),
      })
      + '\n'
      + S.stats_render({
        fps: stats.fps,
        scale: deps.zoom().toFixed(2),
        world: deps.state.timescale,
        map: deps.state.tacticalScale,
        backend: backendCell(stats, backendLabel(deps.gfx())),
      }),
    );
    ui.render();
  };
}

/**
 * The backend cell: what drew the frame, and what it cost the machine.
 *
 * The draw-call and instance counts are the prototype's headline — a whole
 * map in about a dozen calls — so they are on the screen and not only in
 * the console. The GPU figure is absent without
 * `EXT_disjoint_timer_query_webgl2`, and an absent figure is written as a
 * dash rather than as a zero.
 */
function backendCell(stats: FrameStats, label: string): string {
  const gpu = stats.gpuMs === null ? '—' : `${stats.gpuMs.toFixed(1)}ms`;
  return `${label} · dc ${stats.drawCalls} · inst ${stats.instances} · gpu ${gpu}`;
}
